import {
	Dispatch,
	ReactElement,
	RefObject,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	HubCommentPagingInformationModel,
	HubCommentModel,
	HubCommentStatisticsModel
} from "client/Api/Hub/common";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useLocalStorageState,
	useStableImperativeCallback,
	useUserScopedKey,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	CommentListHandle,
	CommentsSortPreference,
	CommentWithRepliesModel
} from "client/Features/Hub/HubCommentSection/HubCommentSection";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { useImageViewer } from "client/Features/Hub/useImageViewer";
import { SortDirection } from "client/Api/common";
import { useUserProfileStore } from "client/Features/Settings/UserProfile/userProfileStore";
import { getCommentsSortDirection, getStructuredComments } from "../../common";
import { MobileCommentFormHandle, MobileCommentMode } from "../MobileComment/MobileComment";
import {
	ensureNotNil,
	isNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { useCommentNavigation } from "./useCommentNavigation";
import { useMobileCommentState } from "./useMobileComment";

export type CommentSectionOptions = {
	getCommentsList: (
		pageSize: number,
		after: HubCommentPagingInformationModel | null,
		sortDirection: SortDirection,
		signal: AbortSignal
	) => Promise<LazyLoadPageResult<HubCommentModel>>;
	sortKey: string;
	onStatisticsUpdate: () => void;
};

export type CommentSectionResult = {
	commentListRef: RefObject<CommentListHandle | null>;
	currentUserInfo: UserIdentityModel;
	imageViewerDialog: ReactElement | null;
	isMobile: boolean;
	mobileCommentFormRef: RefObject<MobileCommentFormHandle | null>;
	mobileMode: MobileCommentMode;
	sortPreference: CommentsSortPreference;
	state: CommentsState;
	structuredComments: readonly CommentWithRepliesModel[];
	commentDeletedHandler: (commentId: number) => void;
	commentReactionHandler: (commentId: number, statistics: HubCommentStatisticsModel) => void;
	commentCreatedHandler: (comment: HubCommentModel) => void;
	commentUpdatedHandler: (comment: HubCommentModel) => void;
	initMobileMode: () => void;
	loadMoreHandler: () => void;
	reloadHandler: () => void;
	mobileModeChangeHandler: (mode: MobileCommentMode) => void;
	onSortPreferenceChange: (sortPreference: CommentsSortPreference) => void;
	viewImageHandler: (imageUrl: string) => void;
};

export function useCommentSection(
	options: CommentSectionOptions
): CommentSectionResult {
	const {
		getCommentsList,
		onStatisticsUpdate,
		sortKey
	} = options;

	const sortPreferencePersistenceKey: string = useUserScopedKey(sortKey);
	const [sortPreference, setSortPreference] = useLocalStorageState<CommentsSortPreference>(
		sortPreferencePersistenceKey,
		CommentsSortPreference.NewestOnTop
	);

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const {
		loadMoreHandler,
		state,
		setState,
		reloadHandler
	} = useCommentsData(
		getCommentsSortDirection(sortPreference),
		PAGE_SIZE,
		getCommentsList
	);

	const [imageViewerDialog, viewImageHandler] = useImageViewer();
	const mobileCommentFormRef: RefObject<MobileCommentFormHandle | null> = useRef<MobileCommentFormHandle>(null);

	const currentUserInfo: UserIdentityModel | null = ensureNotNil(
		useUserProfileStore(x => x.currentUserIdentity)
	);

	const commentDeletedHandler = useCallback(
		(commentId: number): void => {
			setState(
				(x: CommentsState) => updateCommentStateItems(
					x,
					(comments: readonly HubCommentModel[]): readonly HubCommentModel[] => comments.filter(
						c => c.id !== commentId
					)
				)
			);

			onStatisticsUpdate();
		},
		[setState, onStatisticsUpdate]
	);

	const commentReactionHandler = useCallback(
		(
			commentId: number,
			statistics: HubCommentStatisticsModel
		): void => {
			setState(
				(x: CommentsState) => updateCommentStateItems(
					x,
					(comments: readonly HubCommentModel[]): readonly HubCommentModel[] => comments.map(
						c => {
							if (c.id !== commentId) {
								return c;
							}

							return {
								...c,
								statistics
							};
						}
					)
				)
			);
		},
		[setState]
	);

	const formFocusHandler = useCallback(
		(): void => {
			mobileCommentFormRef.current
				?.focusInput();
		},
		[]
	);

	const {
		commentListRef,
		navigateToCommentIdHandler
	} = useCommentNavigation();

	const {
		mobileMode,
		initMobileMode,
		mobileModeChangeHandler,
	} = useMobileCommentState(
		formFocusHandler
	);

	const commentCreatedHandler = useCallback(
		(comment: HubCommentModel): void => {
			onStatisticsUpdate();
			setState(
				(x: CommentsState) => updateCommentStateItems(
					x,
					(comments: readonly HubCommentModel[]): readonly HubCommentModel[] => [
						comment,
						...comments,
					]
				)
			);

			initMobileMode();
			navigateToCommentIdHandler(comment.id);
		},
		[
			onStatisticsUpdate,
			setState,
			initMobileMode,
			navigateToCommentIdHandler
		]
	);

	const commentUpdatedHandler = useCallback(
		(comment: HubCommentModel): void => {
			setState(
				(x: CommentsState) => updateCommentStateItems(
					x,
					(comments: readonly HubCommentModel[]): readonly HubCommentModel[] => comments.map(
						(c: HubCommentModel): HubCommentModel => {
							if (c.id !== comment.id) {
								return c;
							}

							return comment;
						}
					)
				)
			);

			initMobileMode();
		},
		[
			initMobileMode,
			setState
		]
	);

	const viewImageByUrlHandler = useCallback(
		(imageUrl: string): void => {
			viewImageHandler({
				url: imageUrl
			});
		},
		[viewImageHandler]
	);

	const structuredComments = useMemo(
		(): readonly CommentWithRepliesModel[] => {
			return getStructuredComments(state.items);
		},
		[state.items]
	);

	return {
		commentListRef,
		currentUserInfo,
		imageViewerDialog,
		isMobile,
		mobileCommentFormRef,
		mobileMode,
		sortPreference,
		state,
		structuredComments,
		commentDeletedHandler,
		commentReactionHandler,
		commentCreatedHandler,
		commentUpdatedHandler,
		initMobileMode,
		loadMoreHandler,
		mobileModeChangeHandler,
		onSortPreferenceChange: setSortPreference,
		viewImageHandler: viewImageByUrlHandler,
		reloadHandler
	};
}

type CommentsData = {
	state: CommentsState;
	setState: Dispatch<SetStateAction<CommentsState>>;
	loadMoreHandler: () => void;
	reloadHandler: () => void;
};

function useCommentsData(
	sortDirection: SortDirection,
	pageSize: number,
	getCommentsList: (
		pageSize: number,
		after: HubCommentPagingInformationModel | null,
		sortDirection: SortDirection,
		signal: AbortSignal
	) => Promise<LazyLoadPageResult<HubCommentModel>>
): CommentsData {
	const [state, setState] = useState<CommentsState>(initialState);
	const requestCancellationRef: RefObject<AbortController | null> = useRef<AbortController>(null);
	const alertManager: AlertManager = useAlerts();

	const fetchItems = useCallback(
		async (
			after: HubCommentPagingInformationModel | null,
			sortDirection: SortDirection
		): Promise<void> => {
			requestCancellationRef.current
				?.abort();

			const cancellation = new AbortController();
			requestCancellationRef.current = cancellation;

			setState(
				(x: CommentsState): CommentsState => ({
					...x,
					status: "loading"
				})
			);

		try {
			const pageResult: LazyLoadPageResult<HubCommentModel> = await getCommentsList(
				pageSize,
				after,
				sortDirection,
				cancellation.signal
			);

			setState(
				(x: CommentsState): CommentsState => ({
					hasMore: pageResult.hasMore,
					status: "loaded",
					items: [
						...x.items,
						...pageResult.items
					]
				})
			);
		} catch (e) {
			if (isCancelledError(e)) {
				return;
			}

			setState(
				(x: CommentsState): CommentsState => ({
					hasMore: x.hasMore,
					status: "error",
					items: x.items
				})
			);

			console.error(e);
			alertManager.showError(localize("_GeneralErrorMessage"));
		}
	},
	[
		alertManager,
		pageSize,
		setState,
		getCommentsList
	]
);

	const stableFetchItems: (
		after: HubCommentPagingInformationModel | null,
		sortDirection: SortDirection
	) => void = useStableImperativeCallback(fetchItems);

	useEffect(
		(): (() => void) | void => {
			hasImplicitDependencyOn(sortDirection);
			setState(initialState);

			stableFetchItems(null, sortDirection);

			return (): void => requestCancellationRef.current
				?.abort();
		},
		[
			sortDirection,
			setState,
			stableFetchItems
		]
	);

	const loadMoreHandler = useCallback(
		(): void => {
			if (!state.hasMore || state.status === "loading") {
				return;
			}

			fetchItems(
				getPaginationAfterItem(state.items),
				sortDirection
			);
		},
		[
			fetchItems,
			state.hasMore,
			state.status,
			state.items,
			sortDirection
		]
	);

	const reloadHandler = useCallback(
		(): void => {
			if (state.status === "loading") {
				return;
			}

			fetchItems(null, sortDirection);
		},
		[
			state.status,
			fetchItems,
			sortDirection
		]
	);

	return useMemo(
		(): CommentsData => ({
			state,
			setState,
			loadMoreHandler,
			reloadHandler
		}),
		[
			state,
			loadMoreHandler,
			reloadHandler
		]
	);
}

type LoadingStatus = "loading" | "loaded" | "error";

type CommentsState = {
	status: LoadingStatus;
	hasMore: boolean;
	items: readonly HubCommentModel[];
};

const initialState: CommentsState = {
	hasMore: false,
	items: [],
	status: "loading"
};

export function getPagingInformationModelFromComment(
	comment: HubCommentModel
): HubCommentPagingInformationModel {
	return {
		id: comment.id,
		publicationTime: comment.publicationTime
	};
}

function getPaginationAfterItem(
	items: readonly HubCommentModel[]
): HubCommentPagingInformationModel | null {
	const rootItems: HubCommentModel[] = items.filter(x => isNil(x.parentCommentId));

	return rootItems.length > 0
		? getPagingInformationModelFromComment(rootItems[rootItems.length - 1])
		: null;
}

function updateCommentStateItems(
	state: CommentsState,
	updateCallback: (comments: readonly HubCommentModel[]) => readonly HubCommentModel[]
) {
	return {
		...state,
		items: updateCallback(state.items)
	};
}

const PAGE_SIZE = 10;
